import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const WebSocketContext = createContext(null);

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
      base64 += '=';
    }
    const payload = JSON.parse(atob(base64));
    return Date.now() >= payload.exp * 1000 - 10000; // 10s buffer
  } catch {
    return true;
  }
};

export const WebSocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [lastNotification, setLastNotification] = useState(null);
  const listenersRef = useRef(new Set());
  const chatListenersRef = useRef(new Set());

  const subscribe = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  const subscribeChat = useCallback((callback) => {
    chatListenersRef.current.add(callback);
    return () => chatListenersRef.current.delete(callback);
  }, []);

  const getWs = useCallback(() => wsRef.current, []);

  const connect = useCallback(async () => {
    let token = localStorage.getItem('veonfox_token');
    if (!token || !isAuthenticated) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    if (isTokenExpired(token)) {
      const refreshToken = localStorage.getItem('veonfox_refresh');
      if (refreshToken) {
        try {
          const res = await api.post('/auth/refresh', { refreshToken });
          const newToken = res.data.token;
          if (res.data.refreshToken) {
            localStorage.setItem('veonfox_refresh', res.data.refreshToken);
          }
          localStorage.setItem('veonfox_token', newToken);
          token = newToken;
        } catch {
          return;
        }
      }
    }

    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsUrl = baseUrl.replace(/^http/, 'ws') + `/api/ws/notifications?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'notification') {
            setLastNotification(msg.data);
            listenersRef.current.forEach(cb => cb(msg.data));
          } else {
            chatListenersRef.current.forEach(cb => cb(msg));
          }
        } catch {}
      };

      ws.onclose = () => {
        wsRef.current = null;
        const tkn = localStorage.getItem('veonfox_token');
        if (tkn && isAuthenticated) {
          reconnectTimer.current = setTimeout(connect, 5000);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {}
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(connect, 500);
      return () => clearTimeout(timer);
    }
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isAuthenticated, connect]);

  return (
    <WebSocketContext.Provider value={{ lastNotification, subscribe, subscribeChat, getWs }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) return { lastNotification: null, subscribe: () => () => {}, subscribeChat: () => () => {}, getWs: () => null };
  return ctx;
};
